import { useTranslation } from 'react-i18next'
import { Check, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { TemplateMeal } from './types'

interface TemplateCardProps {
  template: TemplateMeal
  onAdd: (id: string) => void
  isPending: boolean
}

export function TemplateCard({ template, onAdd, isPending }: TemplateCardProps) {
  const { t } = useTranslation('templates')

  return (
    <div
      className={cn(
        'bg-surface rounded-xl border border-line p-4 flex items-center justify-between gap-3',
        'active:scale-[0.97] motion-safe:transition-transform',
        template.isAdded && 'border-l-4 border-l-accent bg-accent-tint',
      )}
    >
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-ink truncate">{template.name}</p>
        <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded-full bg-accent-tint text-accent">
          {template.category}
        </span>
      </div>

      {template.isAdded ? (
        <span
          className="flex items-center justify-center min-h-[44px] min-w-[44px] text-ok"
          aria-label={t('card.addedAriaLabel', { name: template.name })}
        >
          <Check
            className="h-6 w-6 motion-safe:animate-[checkFill_200ms_ease-out]"
            aria-hidden="true"
          />
        </span>
      ) : (
        <button
          type="button"
          onClick={() => onAdd(template.id)}
          disabled={isPending}
          aria-label={t('card.addAriaLabel', { name: template.name })}
          className={cn(
            'flex items-center justify-center min-h-[44px] min-w-[44px]',
            'rounded-full border border-accent text-accent',
            'hover:bg-accent-tint active:bg-accent active:text-white',
            'motion-safe:transition-colors disabled:opacity-50',
          )}
        >
          <Plus className="h-5 w-5" aria-hidden="true" />
        </button>
      )}
    </div>
  )
}
